import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import SEOHead from '@/components/feature/SEOHead';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { getCategoryBySlug } from '@/mocks/categories';
import { useEffect } from 'react';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');
  const relatedArticles = getRelatedArticles(slug || '', 3);
  const category = article ? getCategoryBySlug(article.categorySlug) : undefined;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-400">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="mt-4 font-heading text-xl font-bold text-foreground-900">文章未找到</h1>
            <p className="mt-2 text-sm text-foreground-500">该文章可能已被移除或链接有误</p>
            <Link
              to="/"
              className="mt-4 inline-block px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const jsonLdArticle = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishedAt,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '秘密教学漫画内容平台',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.mimijiaoxue-manhua.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 秘密教学漫画`}
        description={article.summary}
        keywords={`秘密教学漫画,${article.tags.join(',')},${article.category}`}
        canonicalUrl={`https://www.mimijiaoxue-manhua.com/article/${article.slug}`}
        ogTitle={article.title}
        ogDescription={article.summary}
        ogType="article"
        ogUrl={`https://www.mimijiaoxue-manhua.com/article/${article.slug}`}
        ogImage={article.image}
      />

      <script type="application/ld+json">{JSON.stringify(jsonLdArticle)}</script>

      <Navbar />
      <ScrollToTop />

      <main className="pt-20 pb-14">
        <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
          <div className="mb-4">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: article.category, path: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
          </div>

          <article>
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="px-3 py-1 text-xs rounded-full bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {article.category}
                </Link>
                <div className="flex items-center gap-3 text-xs text-foreground-400">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.publishedAt}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.readCount >= 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount} 阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                </div>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight">
                {article.title}
              </h1>

              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                {article.summary}
              </p>

              <div className="mt-5 flex items-center gap-2 flex-wrap">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </header>

            <figure className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                title={`${article.title} - 秘密教学漫画`}
                className="w-full aspect-[16/9] object-cover"
              />
            </figure>

            <div
              className="prose-custom"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-6 border-t border-background-200">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground-500">分享本文：</span>
                  <a
                    href="#"
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-500 hover:text-background-50 transition-colors duration-200 cursor-pointer"
                    aria-label="分享到微博"
                    rel="nofollow"
                  >
                    <i className="ri-weibo-line text-sm"></i>
                  </a>
                  <a
                    href="#"
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-500 hover:text-background-50 transition-colors duration-200 cursor-pointer"
                    aria-label="分享到微信"
                    rel="nofollow"
                  >
                    <i className="ri-wechat-line text-sm"></i>
                  </a>
                  <a
                    href="#"
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-500 hover:text-background-50 transition-colors duration-200 cursor-pointer"
                    aria-label="复制链接"
                    rel="nofollow"
                  >
                    <i className="ri-link text-sm"></i>
                  </a>
                </div>
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  查看更多{article.category}内容
                  <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="mt-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((related) => (
                  <ArticleCard key={related.id} {...related} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}